import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';

import useApi from 'hooks/useApi';
import useNotification from 'hooks/useNotification';

import { Button } from 'shared/components/Buttons';
import { LabelRequiredInput, LabelError } from 'shared/components/Labels';
import { colors } from 'shared/defaultTheme';
import DropdownSearch from 'shared/components/DropdownSearch';
import AlertModal from 'shared/components/AlertModal';
import MainSpinner from 'shared/components/MainSpinner';

import { AdvisorFormWrapper } from './styles';

const AdvisorForm = ({ onCancel }) => {
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const { notificationReview } = useSelector((state) => state.notificationReview);
  const { admins } = useSelector((state) => state.auth);
  const newAdvisor = useRef(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedAdvisor, setSelectedAdvisor] = useState('');
  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  newAdvisor.current = watch('advisor', '');

  const { refreshCurrentNotificationReview } = useNotification();

  const [{ loading }, updateNotification] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  useEffect(() => {
    setSelectedAdvisor(newAdvisor.current);
  }, [newAdvisor.current]);

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>{t('notification_change_advisor')}</h3>
        </Col>
      </Row>
    );
  };

  const onEditNotification = async (values) => {
    try {
      const {
        id,
        name,
        date,
        linkToContact,
        responsibles,
        answers,
        docs,
        clientNavisionId,
        clientId,
      } = notificationReview;
      const newData = {
        id,
        createdBy: values.advisor,
        name,
        date,
        linkToContact,
        responsibles,
        description: values.description,
        answers,
        docs,
      };

      const {
        data: { data },
      } = await updateNotification({
        data: newData,
        url: `${window.API_V1}/cli/notifications/${clientNavisionId}/${clientId}/modify`,
      });
      if (!data) {
        throw new Error(
          data.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      await refreshCurrentNotificationReview();
      onCancel();
    } catch (error) {
      setShowError(true);
      setErrorMessage(t('custom_field_operation_error_message'));
    }
  };

  const onSubmit = async (values) => {
    onEditNotification(values);
  };
  return (
    <AdvisorFormWrapper noValidate onSubmit={handleSubmit(onSubmit)}>
      <Title />
      <div className="advisor-form-container my-3 w-100">
        <h3 className="select-advisor-text mb-4">{t('notification_select_advisor_text')}</h3>
        <div className="form dropdown-search-container">
          <LabelRequiredInput
            className="form__form-group-label label-advisor-text"
            label={t('notification_label_advisor')}
            color={colors.black}
          />

          <Controller
            name="advisor"
            control={control}
            defaultValue=""
            rules={{
              required: { value: true, message: t('general_required_field') },
            }}
            render={({ field }) => (
              <DropdownSearch
                id="advisor-dropdown-search"
                options={admins
                  .filter((x) => x.active)
                  .map((item) => ({
                    name: item.nameComplete,
                    value: item.u,
                  }))}
                value={field.value}
                onBlur={field.onBlur}
                onChange={field.onChange}
                error={errors.advisor !== undefined}
                className="advisor-dropdown-search w-100"
                isAdvisor
                selectedAdvisor={selectedAdvisor !== '' && selectedAdvisor.substring(0, 1)}
              />
            )}
          />
          {errors.advisor && <LabelError label={errors.advisor.message} />}
        </div>
      </div>
      <div className="w-100 d-flex justify-content-end">
        <Button
          className="text-uppercase mr-4"
          type="button"
          color="secondary"
          label={t('general_button_cancel')}
          onClick={() => onCancel()}
        />
        <Button
          className="text-uppercase"
          type="submit"
          color="primary"
          label={t('general_button_ok')}
        />
      </div>
      {loading && <MainSpinner />}
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={errorMessage}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
    </AdvisorFormWrapper>
  );
};

AdvisorForm.propTypes = {
  onCancel: PropTypes.func.isRequired,
};

export default AdvisorForm;
